import {
  Autocomplete,
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  createFilterOptions,
  Divider,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useCallback, useEffect, useRef, useState, type HTMLAttributes, type Key } from "react";
import { useSnackbar } from "notistack";
import {
  getSpeakerAssignments,
  saveSpeakerAssignments,
  type SpeakerAssignmentItem,
  type SaveSpeakerAssignment,
} from "../../services/record.service";
import {
  getContacts,
  createContact,
  type Contact,
} from "../../services/contact.service";

type Props = {
  recordId: number;
  canEdit?: boolean;
  /** Publishes persisted assignments after loading or saving for download labels. */
  onAssignmentsSaved?: (assignments: Map<string, string>) => void;
  onDirtyChange?: (dirty: boolean) => void;
};

type ContactOption = Contact | { inputValue: string };

const filterContacts = createFilterOptions<ContactOption>();

type RowState = {
  speakerLabel: string;
  /** The resolved display name: contact name, custom name, or null (still unassigned). */
  displayName: string | null;
  /** The selected contact id, if any. */
  contactId: number | null;
  /** The custom name typed by the user, if any. */
  customName: string | null;
  /** True while this row is in "add new contact" inline mode. */
  addingNew: boolean;
  /** Temporary fields for the inline add form. */
  newName: string;
  newEmail: string;
};

export default function SpeakerAssignmentCard({ recordId, canEdit = true, onAssignmentsSaved, onDirtyChange }: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const [rows, setRows] = useState<RowState[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [dirty, setDirty] = useState(false);
  const operationInFlight = useRef(false);
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);
  const [loadError, setLoadError] = useState(false);
  const [serverCanEdit, setServerCanEdit] = useState(false);
  const editable = canEdit && serverCanEdit;

  // ── Load data ──────────────────────────────────────────────────────────────

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const assignments = await getSpeakerAssignments(recordId);
      setServerCanEdit(assignments.can_edit !== false);
      const names = new Map<string, string>();
      assignments.speakers.forEach((speaker) => {
        const name = speaker.contact_name || speaker.custom_name;
        if (name) names.set(speaker.speaker_label, name);
      });
      onAssignmentsSaved?.(names);

      setRows(
        assignments.speakers.map((s: SpeakerAssignmentItem) => ({
          speakerLabel: s.speaker_label,
          displayName: s.contact_name || s.custom_name || null,
          contactId: s.contact_id ?? null,
          customName: s.custom_name ?? null,
          addingNew: false,
          newName: "",
          newEmail: "",
        })),
      );
      setDirty(false);
    } catch (err) {
      console.error("Failed to load speaker assignments", err);
      enqueueSnackbar("Konuşmacı atamaları yüklenemedi.", { variant: "error" });
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [recordId, enqueueSnackbar, onAssignmentsSaved]);

  useEffect(() => {
    load();
  }, [load]);

  // ── Contact autocomplete helpers ───────────────────────────────────────────

  const refreshContacts = useCallback(async () => {
    setContactsLoading(true);
    try {
      const list = await getContacts();
      setContacts(list);
    } catch {
      enqueueSnackbar("Kişiler yüklenemedi.", { variant: "error" });
    } finally {
      setContactsLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    if (editable) refreshContacts();
  }, [editable, refreshContacts]);

  const handleSelectContact = (index: number, contact: Contact | null) => {
    if (operationInFlight.current || !editable) return;
    setRows((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        contactId: contact?.id ?? null,
        displayName: contact?.name ?? null,
        customName: null,
        addingNew: false,
      };
      return next;
    });
    setDirty(true);
  };

  // ── Inline "add contact" mode ──────────────────────────────────────────────

  const handleStartAddNew = (index: number, name: string) => {
    if (operationInFlight.current || !editable) return;
    setRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], addingNew: true, newName: name, newEmail: "" };
      return next;
    });
  };

  const handleCancelAddNew = (index: number) => {
    if (operationInFlight.current) return;
    setRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], addingNew: false, newName: "", newEmail: "" };
      return next;
    });
  };

  const handleConfirmAddNew = async (index: number) => {
    if (operationInFlight.current || !editable) return;
    const row = rows[index];
    if (!row.newName.trim()) {
      enqueueSnackbar("İsim alanı zorunludur.", { variant: "warning" });
      return;
    }
    operationInFlight.current = true;
    setCreating(true);
    try {
      const created = await createContact({
        name: row.newName.trim(),
        email: row.newEmail.trim() || null,
      });
      setContacts((prev) => [...prev.filter((c) => c.id !== created.id), created]);
      setRows((prev) => {
        const next = [...prev];
        next[index] = {
          ...next[index],
          contactId: created.id,
          displayName: created.name,
          customName: null,
          addingNew: false,
          newName: "",
          newEmail: "",
        };
        return next;
      });
      setDirty(true);
      enqueueSnackbar("Kişi eklendi.", { variant: "success" });
    } catch {
      enqueueSnackbar("Kişi eklenirken hata oluştu.", { variant: "error" });
    } finally {
      operationInFlight.current = false;
      setCreating(false);
    }
  };

  // ── Save ───────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (operationInFlight.current || !editable) return;
    operationInFlight.current = true;
    setSaving(true);
    try {
      const speakers: SaveSpeakerAssignment[] = rows.map((r) => ({
        speaker_label: r.speakerLabel,
        contact_id: r.contactId,
        custom_name: r.customName,
      }));
      await saveSpeakerAssignments(recordId, { speakers });
      setDirty(false);
      enqueueSnackbar("Konuşmacı atamaları kaydedildi.", { variant: "success" });

      // Build the display-name map for the parent
      const map = new Map<string, string>();
      for (const r of rows) {
        if (r.displayName) {
          map.set(r.speakerLabel, r.displayName);
        }
      }
      onAssignmentsSaved?.(map);
    } catch {
      enqueueSnackbar("Konuşmacı atamaları kaydedilirken hata oluştu.", {
        variant: "error",
      });
    } finally {
      operationInFlight.current = false;
      setSaving(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Card sx={{ p: 3 }}>
        <Stack alignItems="center" spacing={1}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            Konuşmacılar yükleniyor...
          </Typography>
        </Stack>
      </Card>
    );
  }

  if (loadError) {
    return (
      <Card sx={{ p: 3 }}>
        <Alert severity="error" action={<Button onClick={load}>Yeniden dene</Button>}>
          Konuşmacı atamaları yüklenemedi.
        </Alert>
      </Card>
    );
  }

  if (rows.length === 0) {
    return (
      <Card sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Icon icon="mdi:account-voice" width={22} />
            <Typography variant="subtitle1" fontWeight={600}>
              Konuşmacı Atama
            </Typography>
          </Stack>
          <Divider />
          <Stack alignItems="center" spacing={1} sx={{ py: 3 }}>
            <Icon icon="mdi:account-off-outline" width={40} color="#919EAB" />
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Bu kayıtta konuşmacı etiketi bulunamadı.
            </Typography>
            <Typography variant="caption" color="text.disabled" textAlign="center">
              Transkripsiyon tamamlandıktan sonra konuşmacı etiketleri burada görünecek.
              <br />
              Atanacak konuşmacı bulunamadı.
            </Typography>
          </Stack>
        </Stack>
      </Card>
    );
  }

  return (
    <Card sx={{ p: 3 }}>
      <Stack spacing={2}>
        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={1}>
          <Icon icon="mdi:account-voice" width={22} />
          <Typography variant="subtitle1" fontWeight={600}>
            Konuşmacı Atama
          </Typography>
          <Typography variant="caption" color="text.secondary">
            (Konuşmacı etiketlerini kişilerle eşleştirin)
          </Typography>
        </Stack>

        <Divider />

        {!editable && <Alert severity="info">Konuşmacı atamalarını yalnızca kayıt sahibi değiştirebilir.</Alert>}
        <Stack
          component="fieldset"
          spacing={2}
          disabled={saving || creating || !editable}
          aria-busy={saving || creating}
          sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}
        >
        {/* Rows */}
        {rows.map((row, i) => (
          <Box key={row.speakerLabel}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              alignItems={{ sm: "center" }}
            >
              {/* Speaker label badge */}
              <Chip
                label={row.speakerLabel}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ minWidth: 48, fontWeight: 600, flexShrink: 0 }}
              />

              {row.addingNew ? (
                /* ── Inline add-contact form ── */
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  flex={1}
                  alignItems={{ sm: "center" }}
                >
                  <TextField
                    size="small"
                    label="Ad Soyad"
                    placeholder="Ad Soyad"
                    value={row.newName}
                    onChange={(e) => {
                      setRows((prev) => {
                        const next = [...prev];
                        next[i] = { ...next[i], newName: e.target.value };
                        return next;
                      });
                    }}
                    sx={{ minWidth: 180 }}
                    autoFocus
                  />
                  <TextField
                    size="small"
                    label="E-posta (isteğe bağlı)"
                    placeholder="E-posta (isteğe bağlı)"
                    value={row.newEmail}
                    onChange={(e) => {
                      setRows((prev) => {
                        const next = [...prev];
                        next[i] = { ...next[i], newEmail: e.target.value };
                        return next;
                      });
                    }}
                    sx={{ minWidth: 200 }}
                  />
                  <Stack direction="row" spacing={0.5}>
                    <IconButton
                      size="small"
                      color="primary"
                      aria-label="Yeni kişiyi ekle"
                      onClick={() => handleConfirmAddNew(i)}
                    >
                      {creating ? <CircularProgress size={18} /> : <Icon icon="mdi:check" width={18} />}
                    </IconButton>
                    <IconButton size="small" aria-label="Kişi eklemekten vazgeç" onClick={() => handleCancelAddNew(i)}>
                      <Icon icon="mdi:close" width={18} />
                    </IconButton>
                  </Stack>
                </Stack>
              ) : (
                /* ── Contact autocomplete ── */
                <Autocomplete<ContactOption>
                  disabled={saving || creating || !editable}
                  size="small"
                  options={contacts}
                  value={
                    contacts.find((c) => c.id === row.contactId) ?? null
                  }
                  onChange={(_e, val) => {
                    if (val && "inputValue" in val) {
                      handleStartAddNew(i, val.inputValue);
                    } else {
                      handleSelectContact(i, val);
                    }
                  }}
                  filterOptions={(options, params) => {
                    const filtered = filterContacts(options, params);
                    const name = params.inputValue.trim();
                    if (!name || !contacts.some((contact) =>
                      contact.name.trim().toLocaleLowerCase("tr") === name.toLocaleLowerCase("tr"),
                    )) {
                      filtered.push({ inputValue: name });
                    }
                    return filtered;
                  }}
                  getOptionLabel={(option) => {
                    if ("inputValue" in option) {
                      return option.inputValue ? `Yeni ekle: "${option.inputValue}"` : "Yeni kişi ekle";
                    }
                    return option.email
                      ? `${option.name} (${option.email})`
                      : option.name;
                  }}
                  renderOption={({ key, ...props }: HTMLAttributes<HTMLLIElement> & { key?: Key }, option) => (
                    <li key={key} {...props}>
                      {"inputValue" in option ? (
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ color: "primary.main" }}>
                          <Icon icon="mdi:account-plus-outline" width={20} />
                          <Typography variant="body2" fontWeight={600}>
                            {option.inputValue ? `Yeni ekle: "${option.inputValue}"` : "Yeni kişi ekle"}
                          </Typography>
                        </Stack>
                      ) : (
                        option.email ? `${option.name} (${option.email})` : option.name
                      )}
                    </li>
                  )}
                  isOptionEqualToValue={(opt, val) =>
                    !("inputValue" in opt) && !("inputValue" in val) && opt.id === val.id
                  }
                  loading={contactsLoading}
                  onOpen={() => refreshContacts()}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      inputProps={{ ...params.inputProps, "aria-label": `${row.speakerLabel} için kişi` }}
                      placeholder="Kişi seçin veya yazın..."
                    />
                  )}
                  sx={{ flex: 1, minWidth: 200 }}
                  noOptionsText="Kişi bulunamadı"
                  loadingText="Kişiler yükleniyor..."
                />
              )}

              {/* Action buttons */}
              <Stack direction="row" spacing={0.5} flexShrink={0}>
                {!row.addingNew && (
                  <>
                    {row.contactId || row.customName ? (
                      <Tooltip title="Atamayı kaldır">
                        <IconButton
                          size="small"
                          color="default"
                          onClick={() =>
                            handleSelectContact(i, null)
                          }
                        >
                          <Icon icon="mdi:close-circle-outline" width={18} />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                  </>
                )}
              </Stack>
            </Stack>

            {/* Show assigned name below the row */}
            {row.displayName && !row.addingNew && (
              <Typography
                variant="caption"
                color="success.main"
                sx={{ ml: 7, mt: 0.5, display: "block" }}
              >
                → {row.displayName}
                {!row.contactId && " (özel isim)"}
              </Typography>
            )}
          </Box>
        ))}
        </Stack>

        {/* Save button */}
        <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1.5}>
          {dirty && (
            <Typography variant="body2" color="warning.main">
              Kaydedilmemiş değişiklikler var — indirilen dosyalara yansımaz
            </Typography>
          )}
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!editable || !dirty || saving || creating || rows.some((row) => row.addingNew)}
            startIcon={
              saving ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <Icon icon="mdi:content-save-outline" width={18} />
              )
            }
          >
            {saving ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </Stack>
      </Stack>
    </Card>
  );
}